// 部門維護的「樹狀圖／列表」頁籤容器與樹（department.jsp tabs-container，:633-650）。
//
// legacy 行為（行號親驗 2026-10-01）：
//   - 頁籤列＝`ul.nav.nav-tabs#footerTab > li > a`（:635-637）：a 帶 data-toggle="tab"、
//     href="#tab-1"（樹狀圖）/"#tab-2"（列表）、onclick="changeTab('detail'/'table')"
//     （changeTab :550-555 只寫 cookie `department.firstGoTab`）；active 標示由 bootstrap
//     tab JS 掛在 li/pane 的 class（執行後 raw：tabTable li.active＋tab-2 pane.active；
//     無 cookie 預設列表，doQuery :206-209 `.eq(1).tab('show')`）。
//   - 兩個 tab-pane 都常駐 DOM（:640-650；非作用中 pane 由 bootstrap CSS 隱藏，不是
//     inline display:none）——jstree 在 doQuery 回來後即建立（不論作用中與否）。
//   - 樹＝jstree（#A1，:234-263）：根節點＝醫院名稱（id=0），一級部門 create_node 掛根下
//     （:259-264 loaded.jstree），子部門依 parentId 巢狀、逐層依 sort（:214-217）。
//     節點 DOM＝li > i.jstree-ocl（摺疊 icon）＋a.jstree-anchor（href="#"）> 文字；
//     子層 ul.jstree-children（normalize 後 i 攤平、class 丟棄＝比對面 ul>li>a>#text）。
//   - select_node（:265-277）：左鍵（button==1）且 level>=2 → showModal＝點節點名稱開修改
//     modal（一級部門 level=2、子部門 level=3）；根節點（level=1）無動作。
//   - 右鍵選單 customMenu（:289-316）：非根節點（parents.length>1）顯示「刪除」
//     （checkDeleteUsage）與「新增子部門」（showModal(0,node,level)）；根節點無項目。
//     選單項順序＝新增子部門在前（customMenu 的 $.extend(createSubDepartment, items)）。
//   - 頁籤狀態存 cookie `department.firstGoTab`（:551-554）；雛形沒有 cookie 機制，
//     改存 localStorage 同名 key（語意對齊，批次 12 既有裁決）。
//
// W10（批次 42，issue #40，裁決③案 2 鏡像）：頁籤列從 Button×2 改鏡像 ul.nav.nav-tabs>li>a
// （data-toggle／href／aria-expanded 照畫——normalize 丟 data-* 與 aria-*，參與比對的只有
// li>a>href>#text；active 用 class 表達，class 同樣不參與比對）；樹從 div/li/button 改
// jstree 結構 ul>li>a（i 照 jstree 畫——INLINE_PRESENTATION 攤平，摺疊 onClick 挂 i 上
// 不入 DOM，摺疊箭頭走 ::before 偽元素＝raw DOM 序列化不含，序位零差）；樹與表格常駐
// 渲染（legacy 非 inline 隱藏、normalize 看不到，用 Tailwind hidden class 對齊——inline
// display:none 才會觸發 hidden 標記（normalize-dom R7），class 不會）；節點動作（新增
// 子部門/刪除）改右鍵選單（customMenu 鏡像；選單互動時才渲染，raw 擷取面無差）。樹節點
// 文字（本機帳號 vs seed 醫院/部門名）＝GNL-006 AD 殘留，照實不修。
//
// 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】

import { useState, type ReactElement, type MouseEvent as ReactMouseEvent } from 'react';
import { t } from '@/messages/zh-TW';
import { legacyContractFieldsFor, optionsFor } from '@/mocks';
import type { Row } from '@/mocks/types';
import {
  deleteWithUsageCheck,
  type BehaviorFlowContext,
  type UsageCheckTexts,
} from '../templates/page-behaviors';

/** legacy 的 cookie key（department.jsp:551-554），雛形存 localStorage、值同款。 */
const TAB_STORAGE_KEY = 'department.firstGoTab';
/** legacy 的值：detail＝樹狀圖、table＝列表；沒有值時預設列表（department.jsp:206-209）。 */
type DepartmentTab = 'detail' | 'table';

function readInitialTab(): DepartmentTab {
  try {
    return window.localStorage.getItem(TAB_STORAGE_KEY) === 'detail' ? 'detail' : 'table';
  } catch {
    return 'table';
  }
}

export interface DepartmentTableAreaProps {
  /** 頁籤「列表」的內容＝CrudListPage 的 DataTableShell。 */
  readonly table: ReactElement;
  readonly ctx: BehaviorFlowContext;
  readonly texts: UsageCheckTexts;
  /** 這頁目前的資料列（跟著模板的 list 走，樹與表格同步）。 */
  readonly rows: readonly Row[];
}

/** 頁籤容器：樹狀圖／列表切換；「列表」塞模板的表格，樹狀圖塞 DepartmentTree。 */
export function DepartmentTableArea({ table, ctx, texts, rows }: DepartmentTableAreaProps): ReactElement {
  const [tab, setTab] = useState<DepartmentTab>(readInitialTab);
  const switchTab = (next: DepartmentTab) => {
    setTab(next);
    try {
      window.localStorage.setItem(TAB_STORAGE_KEY, next);
    } catch {
      // localStorage 不可用（隱私視窗等）時只切換狀態，不持久化。
    }
  };
  // 頁籤點擊（legacy onclick="changeTab(...)"，a 的 href 只為鏡像）。
  const switchTabFromAnchor = (next: DepartmentTab) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    switchTab(next);
  };
  return (
    <div data-ui-id={`${ctx.pageId}-tabs`} className="tabs-container">
      {/* W10 鏡像 department.jsp:635-637（ul.nav.nav-tabs>li>a）。normalize 面只有
          li>a>href>#text（data-* 與 aria-*／class／id 都丟）；active 用 class 表達＝legacy
          bootstrap tab 同機制（class 也不參與比對），語意保真由 uiId＋state 承擔。 */}
      <ul data-ui-id={`${ctx.pageId}-tabs-header`} className="nav nav-tabs">
        <li className={tab === 'detail' ? 'active' : ''}>
          <a
            data-ui-id={`${ctx.pageId}-tab-detail`}
            data-toggle="tab"
            href="#tab-1"
            aria-expanded={tab === 'detail'}
            onClick={switchTabFromAnchor('detail')}
          >
            樹狀圖
          </a>
        </li>
        <li className={tab === 'table' ? 'active' : ''}>
          <a
            data-ui-id={`${ctx.pageId}-tab-table`}
            data-toggle="tab"
            href="#tab-2"
            aria-expanded={tab === 'table'}
            onClick={switchTabFromAnchor('table')}
          >
            列表
          </a>
        </li>
      </ul>
      {/* W10：兩 pane 常駐渲染（department.jsp:640-650 靜態原文兩 pane 皆在 DOM；非作用中
          pane 由 bootstrap CSS 隱藏＝normalize 不可見，react 用 Tailwind hidden class 對齊；
          inline display:none 才會觸發 hidden 標記（normalize-dom R7），class 不會）。
          樹渲染時機依 legacy：doQuery 後即建（不論作用中）＝rows 到位後即渲染。 */}
      <div data-ui-id={`${ctx.pageId}-tab-content`} className="tab-content">
        <div
          data-ui-id={`${ctx.pageId}-tab-pane-detail`}
          className={tab === 'detail' ? 'block' : 'hidden'}
        >
          <DepartmentTree ctx={ctx} texts={texts} rows={rows} />
        </div>
        <div
          data-ui-id={`${ctx.pageId}-tab-pane-table`}
          className={tab === 'table' ? 'block' : 'hidden'}
        >
          {table}
        </div>
      </div>
    </div>
  );
}

/** 樹節點：legacy jstree node 的 React 鏡像（依 parentId 組階層，逐層依排序）。 */
interface TreeNode {
  readonly row: Row;
  readonly children: TreeNode[];
}

function buildTree(rows: readonly Row[]): TreeNode[] {
  const byParent = new Map<string, Row[]>();
  for (const row of rows) {
    const parent = String(row['parentId'] ?? '0');
    const bucket = byParent.get(parent);
    if (bucket === undefined) {
      byParent.set(parent, [row]);
    } else {
      bucket.push(row);
    }
  }
  const sortRows = (list: readonly Row[]): readonly Row[] =>
    [...list].sort((left, right) => Number(left['score'] ?? 0) - Number(right['score'] ?? 0));
  const buildLevel = (parentId: string): TreeNode[] =>
    sortRows(byParent.get(parentId) ?? []).map((row) => ({
      row,
      children: buildLevel(String(row.id ?? '')),
    }));
  return buildLevel('0');
}

/** 樹狀圖：根節點（醫院）＋一級部門／子部門巢狀（jstree #A1 結構，department.jsp:234-263）。 */
export function DepartmentTree({
  ctx,
  texts,
  rows,
}: {
  ctx: BehaviorFlowContext;
  texts: UsageCheckTexts;
  rows: readonly Row[];
}): ReactElement {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  // W10：右鍵選單（customMenu :289-316 鏡像）＝開啟中節點；互動時才渲染，raw 擷取面無差。
  const [menu, setMenu] = useState<
    { readonly key: string; readonly row: Row; readonly level: number; readonly x: number; readonly y: number } | null
  >(null);

  const treeCtx: BehaviorFlowContext = {
    ...ctx,
    reload: ctx.reload,
  };

  const tree = buildTree(rows);
  // 根節點文字＝查詢表單「醫院」下拉的第一個選項（legacy：setOrganizationElement 預設選第一個，
  // 樹根顯示它的名稱，department.jsp:206-214）。雛形從 mock 層的動態選項取值，不硬編。
  // 文字值（本機帳號 vs seed 醫院名）＝GNL-006 AD，照實不修。
  const organizationField = legacyContractFieldsFor(ctx.pageId)?.find(
    (field) => field.name === 'organizationId',
  );
  const rootLabel =
    organizationField === undefined
      ? t('search.keyword')
      : (optionsFor(ctx.pageId, organizationField)[0]?.text ?? '');

  const toggle = (key: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const closeMenu = () => setMenu(null);
  const openMenu = (key: string, row: Row, level: number) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
    // legacy 是右鍵（contextmenu plugin）；preventDefault 擋掉瀏覽器原生選單。
    event.preventDefault();
    setMenu({ key, row, level, x: event.clientX, y: event.clientY });
  };

  /**
   * jstree 節點的鏡像（department.jsp raw：li > i.jstree-ocl + a.jstree-anchor > 文字，
   * 子層 ul.jstree-children）：i 攤平、a 的 href="#"（jstree-anchor 同值）；摺疊 onClick
   * 挂 i、箭頭走 ::before 偽元素（raw DOM 序列化不含，i 保持空元素＝序位零差）。
   * select_node :265-277＝左鍵且 level>=2 開修改 modal（editEntry:'tree'）；
   * 右鍵 customMenu :289-316＝非根節點開選單。
   */
  const renderNode = (node: TreeNode, level: number): ReactElement => {
    const key = String(node.row.id ?? '');
    const rowKey = toKebabKey(key);
    const isCollapsed = collapsed.has(key);
    const hasChildren = node.children.length > 0;
    return (
      <li
        key={key}
        data-ui-id={`${ctx.pageId}-tree-node-${rowKey}`}
        className={`jstree-node ${isCollapsed ? 'jstree-closed' : 'jstree-open'}`}
      >
        <i
          data-ui-id={hasChildren ? `${ctx.pageId}-tree-node-${rowKey}-toggle` : undefined}
          className={`jstree-icon jstree-ocl cursor-pointer select-none ${
            hasChildren
              ? isCollapsed
                ? "before:content-['▸']"
                : "before:content-['▾']"
              : ''
          }`}
          onClick={hasChildren ? () => toggle(key) : undefined}
        />
        {/* department.jsp:265-277 select_node：點節點名稱開修改 modal（樹入口無停用鎖，
            showModal :41-136 無 isEnable 分支——editEntry='tree' 標記給 modal 判讀）。 */}
        <a
          data-ui-id={`${ctx.pageId}-tree-node-${rowKey}-name`}
          href="#"
          className="jstree-anchor cursor-pointer"
          onClick={(event) => {
            event.preventDefault();
            if (level >= 2) {
              ctx.openEdit(node.row, { editEntry: 'tree' });
            }
          }}
          onContextMenu={level >= 2 ? openMenu(key, node.row, level) : undefined}
        >
          {String(node.row['unit'] ?? '')}
        </a>
        {hasChildren ? (
          <ul className={`jstree-children ${isCollapsed ? 'hidden' : ''}`}>
            {node.children.map((child) => renderNode(child, level + 1))}
          </ul>
        ) : null}
      </li>
    );
  };

  return (
    <div data-ui-id={`${ctx.pageId}-tree`} className="jstree jstree-1 jstree-default" role="tree">
      <ul
        data-ui-id={`${ctx.pageId}-tree-list`}
        className="jstree-container-ul jstree-children jstree-contextmenu"
      >
        {/* 根節點（醫院，jstree id=0）：select_node level=1 無動作（:271-273）、右鍵無項目
            （customMenu :292/:309 的 parents 條件）；文字＝rootLabel（GNL-006 AD）。 */}
        <li
          data-ui-id={`${ctx.pageId}-tree-node-0`}
          className={`jstree-node ${collapsed.has('0') ? 'jstree-closed' : 'jstree-open'}`}
        >
          <i
            data-ui-id={`${ctx.pageId}-tree-node-0-toggle`}
            className={`jstree-icon jstree-ocl cursor-pointer select-none ${
              collapsed.has('0') ? "before:content-['▸']" : "before:content-['▾']"
            }`}
            onClick={() => toggle('0')}
          />
          <a href="#" className="jstree-anchor">
            {rootLabel}
          </a>
          <ul className={`jstree-children ${collapsed.has('0') ? 'hidden' : ''}`}>
            {tree.map((node) => renderNode(node, 2))}
          </ul>
        </li>
      </ul>
      {/* 右鍵選單（customMenu :289-316）：一級/子部門＝「新增子部門」（Create 在前，
          showModal(0,node,level) 的 preset 同舊 add-child 鈕）＋「刪除」（separator_before，
          checkDeleteUsage :298 → deleteWithUsageCheck）。 */}
      {menu === null ? null : (
        <div
          data-ui-id={`${ctx.pageId}-tree-context-menu`}
          className="fixed z-50 min-w-32 rounded border border-border bg-surface py-1 text-sm shadow-md"
          style={{ left: menu.x, top: menu.y }}
        >
          <a
            data-ui-id={`${ctx.pageId}-tree-menu-add-child`}
            href="#"
            className="block cursor-pointer px-3 py-1 hover:bg-surface-2"
            onClick={(event) => {
              event.preventDefault();
              closeMenu();
              // department.jsp:313-315 showModal(0,node,level)；:62 setOrganizationElement 同步帶
              // 父部門的醫院——preset 一併帶 organizationId，讓 modal 免等非同步清單（R2-F4）。
              ctx.openCreate({
                parentId: menu.key,
                id: '',
                organizationId: String(menu.row['organizationId'] ?? ''),
              });
            }}
          >
            新增子部門
          </a>
          <a
            data-ui-id={`${ctx.pageId}-tree-menu-delete`}
            href="#"
            className="block cursor-pointer px-3 py-1 hover:bg-surface-2"
            onClick={(event) => {
              event.preventDefault();
              closeMenu();
              // department.jsp:298 checkDeleteUsage(node.id,'detail')。
              void deleteWithUsageCheck(treeCtx, menu.row, texts);
            }}
          >
            {t('action.delete')}
          </a>
        </div>
      )}
    </div>
  );
}

/** data-ui-id 用的 kebab 化（沿用 shared.toUiKey 的規則，這裡不 import 以免循環）。 */
function toKebabKey(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .toLowerCase()
    .replace(/^-+|-+$/g, '');
}
